/**
 * Parses [TOOL_START]...[TOOL_END] markers from Gemini output transcription
 * and extracts tool calls for client-side execution.
 *
 * In transcription mode, Gemini outputs tool call markers as text that
 * appears in the transcription but is NOT spoken aloud to the student.
 * This parser extracts those markers and returns both the clean text
 * (for logging/display) and the parsed tool calls (for execution).
 */

export interface ParsedToolCall {
  name: string;
  args: Record<string, string | number>;
}

export interface TranscriptionParseResult {
  /** Clean text with tool markers stripped */
  cleanText: string;
  /** Tool calls extracted from the text */
  toolCalls: ParsedToolCall[];
}

const MARKER_START = '[TOOL_START]';
const MARKER_END = '[TOOL_END]';

/**
 * Parse a tool marker content string.
 * Format examples:
 *   "write_board | type=\"step\" | content=\"x squared\""
 *   "write_board | type=\"step\" | render=\"number_line\" | content=\"from -5 to 5, open at -2 and closed at 3\""
 *   "write_board | type=\"step\" | render=\"coordinate_plane\" | content=\"plot A at 2,3 and B at -1,4 and draw segment AB\""
 *   "write_board | type=\"step\" | render=\"geometry\" | content=\"draw triangle ABC with A(0,0), B(4,0), C(2,3)\""
 *   "clear_board"
 *   "award_star | checkpoint=1"
 */
/** write_board type values — if the model uses one of these as the tool name, normalize to write_board */
const WRITE_BOARD_TYPES = new Set(['step', 'concept', 'question', 'heading', 'subheading']);

function parseToolMarker(raw: string): ParsedToolCall | null {
  const firstPipe = raw.indexOf('|');
  let name = (firstPipe === -1 ? raw : raw.slice(0, firstPipe)).trim();
  if (!name) return null;

  const args: Record<string, string | number> = {};

  if (firstPipe !== -1) {
    const argsStr = raw.slice(firstPipe + 1);
    // Match key="quoted value" or key=unquoted_value
    const argPattern = /(\w+)\s*=\s*(?:"((?:[^"\\]|\\.)*)"|(\S+))/g;
    let match;
    while ((match = argPattern.exec(argsStr)) !== null) {
      const key = match[1];
      const isQuoted = match[2] !== undefined;
      const value = isQuoted ? match[2] : match[3];
      // Only convert unquoted values to numbers (e.g. checkpoint=1).
      // Quoted values stay as strings even if numeric (e.g. content="18").
      if (!isQuoted) {
        const num = Number(value);
        args[key] = !isNaN(num) && value === String(num) ? num : value;
      } else {
        args[key] = value;
      }
    }
  }

  // Normalize: if the model used a type value as the tool name (e.g. "concept" instead of "write_board"),
  // treat it as write_board with that type.
  if (WRITE_BOARD_TYPES.has(name)) {
    args.type = args.type ?? name;
    name = 'write_board';
  }

  return { name, args };
}

/**
 * Streaming parser that buffers transcription chunks and extracts
 * [TOOL_START]...[TOOL_END] tool call markers.
 *
 * Handles:
 * - Markers split across multiple chunks
 * - Multiple markers in a single chunk
 * - Partial markers at chunk boundaries
 * - Dedup of identical calls within a short window (in case markers
 *   appear in both model_turn_text and output_transcription)
 */
export class TranscriptionToolParser {
  private buffer = '';
  private recentCallKeys = new Set<string>();

  /**
   * Feed a chunk of transcription text.
   * Returns clean text (markers stripped) and any completed tool calls.
   */
  push(chunk: string): TranscriptionParseResult {
    this.buffer += chunk;
    return this.extract();
  }

  /** Flush remaining buffer as clean text (e.g. at session end). */
  flush(): TranscriptionParseResult {
    const remaining = this.buffer.trim();
    this.buffer = '';
    return { cleanText: remaining, toolCalls: [] };
  }

  /** Reset the parser state for a new session. */
  reset(): void {
    this.buffer = '';
    this.recentCallKeys.clear();
  }

  private extract(): TranscriptionParseResult {
    const toolCalls: ParsedToolCall[] = [];
    const cleanParts: string[] = [];

    while (true) {
      const startIdx = this.buffer.indexOf(MARKER_START);

      if (startIdx === -1) {
        // No marker start found — emit text, keeping any partial marker suffix
        const partialIdx = this.findPartialMarkerIndex();
        if (partialIdx !== -1) {
          cleanParts.push(this.buffer.slice(0, partialIdx));
          this.buffer = this.buffer.slice(partialIdx);
        } else {
          cleanParts.push(this.buffer);
          this.buffer = '';
        }
        break;
      }

      const endIdx = this.buffer.indexOf(MARKER_END, startIdx + MARKER_START.length);

      if (endIdx === -1) {
        // Incomplete marker — keep from [TOOL_START] onwards in buffer
        cleanParts.push(this.buffer.slice(0, startIdx));
        this.buffer = this.buffer.slice(startIdx);
        break;
      }

      // Complete marker found
      cleanParts.push(this.buffer.slice(0, startIdx));

      const markerContent = this.buffer.slice(
        startIdx + MARKER_START.length,
        endIdx,
      ).trim();

      const parsed = parseToolMarker(markerContent);
      if (parsed) {
        // Dedup: skip if same call was seen in the last 3s
        // (guards against duplicate delivery from multiple transcription sources)
        const callKey = JSON.stringify(parsed);
        if (!this.recentCallKeys.has(callKey)) {
          this.recentCallKeys.add(callKey);
          setTimeout(() => this.recentCallKeys.delete(callKey), 3000);
          toolCalls.push(parsed);
        }
      }

      this.buffer = this.buffer.slice(endIdx + MARKER_END.length);
    }

    return {
      cleanText: cleanParts.join('').trim(),
      toolCalls,
    };
  }

  /**
   * Find the start index of a partial marker suffix at the end of the buffer.
   * E.g. if buffer ends with "[TOOL_" or "[TOOL_STA", keep it for next chunk.
   */
  private findPartialMarkerIndex(): number {
    const markers = [MARKER_START, MARKER_END];
    for (const marker of markers) {
      // Check longest prefix first for earliest match
      for (let len = marker.length - 1; len >= 1; len--) {
        if (this.buffer.endsWith(marker.slice(0, len))) {
          return this.buffer.length - len;
        }
      }
    }
    return -1;
  }
}
